import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarDays, ClipboardList } from 'lucide-react';
import { get, post, qs } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { cn, humanize, timeOnly, todayIso } from '@/lib/utils';
import { DataTable, FilterBar, FilterField, StatTile, Tabs } from '@/components/ui/layout';
import { Button, DatePicker, MonthPicker, Select, StatusBadge } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { currentMonth, hoursLabel, minutesLabel, monthRange, SHIFT_TONE, useDepartmentOptions } from './hr-shared';

interface DayRow {
  employee: { id: string; name: string; employeeNo: string; designation: string | null };
  attendance: {
    id: string;
    status: string;
    checkInAt: string | null;
    checkOutAt: string | null;
    lateMinutes: number;
    overtimeMinutes: number;
  } | null;
  shift: { shift: string; startsAt: string; endsAt: string } | null;
  leaveType: string | null;
}

interface SummaryRow {
  employeeId: string;
  name: string;
  employeeNo: string;
  present: number;
  absent: number;
  leave: number;
  half_day: number;
  lateDays: number;
  lateMinutes: number;
  overtimeMinutes: number;
}

interface MarkBody {
  employeeId: string;
  workDate: string;
  status: string;
  checkInAt?: string;
  checkOutAt?: string;
}

function effectiveStatus(row: DayRow): string {
  if (row.attendance) return row.attendance.status;
  return row.leaveType ? 'leave' : 'unmarked';
}

function DayRegister() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const manage = can('hr.attendance.manage');
  const [date, setDate] = useState(todayIso());
  const [departmentId, setDepartmentId] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const departments = useDepartmentOptions(can('department.read'));

  const sheet = useQuery({
    queryKey: ['attendance', 'day', date, departmentId],
    queryFn: () => get<{ date: string; rows: DayRow[] }>(`/hr/attendance/day${qs({ date, departmentId: departmentId || undefined })}`),
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['attendance'] });

  const mark = useMutation({
    mutationFn: (body: MarkBody) => {
      setBusy(body.employeeId);
      return post('/hr/attendance', { ...body, source: 'manual' });
    },
    onSuccess: () => void refresh(),
    onError: (error) => toast.error('Could not record attendance', describeError(error)),
    onSettled: () => setBusy(null),
  });

  const markAll = useMutation({
    mutationFn: (employeeIds: string[]) => post<{ created: number }>('/hr/attendance/bulk', { workDate: date, employeeIds, status: 'present' }),
    onSuccess: (result) => {
      toast.success(`${result.created} marked present`);
      void refresh();
    },
    onError: (error) => toast.error('Could not mark everyone', describeError(error)),
  });

  const rows = sheet.data?.rows ?? [];
  const statuses = rows.map(effectiveStatus);
  const count = (status: string) => statuses.filter((s) => s === status).length;
  const late = rows.filter((row) => (row.attendance?.lateMinutes ?? 0) > 0).length;
  const unmarked = rows.filter((row) => effectiveStatus(row) === 'unmarked').map((row) => row.employee.id);

  return (
    <>
      <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile label="Present" value={count('present') + count('half_day')} tone="success" sub={count('half_day') ? `${count('half_day')} half day` : undefined} />
        <StatTile label="Absent" value={count('absent')} tone={count('absent') ? 'destructive' : 'default'} />
        <StatTile label="Late" value={late} tone={late ? 'warning' : 'default'} />
        <StatTile label="On leave" value={count('leave')} sub={unmarked.length ? `${unmarked.length} not yet marked` : 'Everyone is marked'} />
      </div>

      <FilterBar
        trailing={
          manage && (
            <>
              <Button
                variant="outline"
                disabled={unmarked.length === 0}
                loading={markAll.isPending}
                onClick={() => markAll.mutate(unmarked)}
              >
                Mark everyone present{unmarked.length ? ` (${unmarked.length})` : ''}
              </Button>
              <Button onClick={() => navigate(`/people/attendance/new${qs({ date })}`)}>Record a day</Button>
            </>
          )
        }
      >
        <FilterField label="Date">
          <DatePicker value={date} onChange={setDate} className="w-40" />
        </FilterField>
        {departments.data && (
          <FilterField label="Department">
            <Select className="w-52" value={departmentId} onChange={(e) => setDepartmentId(e.target.value)}>
              <option value="">All departments</option>
              {departments.data.map((department) => (
                <option key={department.id} value={department.id}>
                  {department.name}
                </option>
              ))}
            </Select>
          </FilterField>
        )}
      </FilterBar>

      <DataTable
        rows={rows}
        loading={sheet.isLoading}
        refreshing={sheet.isFetching}
        error={sheet.error}
        rowKey={(row) => row.employee.id}
        empty="No active employees."
        rowClassName={(row) => (effectiveStatus(row) === 'unmarked' ? 'opacity-80' : undefined)}
        columns={[
          {
            header: 'Person',
            sort: (row) => row.employee.name,
            cell: (row) => (
              <div>
                <p>{row.employee.name}</p>
                <p className="text-xs font-normal text-muted-foreground">{row.employee.designation ?? row.employee.employeeNo}</p>
              </div>
            ),
          },
          {
            header: 'Shift',
            hideBelow: 'md',
            sort: (row) => row.shift?.startsAt ?? '',
            cell: (row) =>
              row.shift ? (
                <span className={cn('inline-flex items-center gap-1.5 rounded-md border px-1.5 py-0.5 text-[11px] font-medium', SHIFT_TONE[row.shift.shift])}>
                  {humanize(row.shift.shift)}
                  <span className="font-normal tabular">
                    {timeOnly(row.shift.startsAt)}–{timeOnly(row.shift.endsAt)}
                  </span>
                </span>
              ) : (
                <span className="text-xs text-muted-foreground">Not rostered</span>
              ),
          },
          { header: 'In', sort: (row) => row.attendance?.checkInAt ?? '', cell: (row) => (row.attendance?.checkInAt ? timeOnly(row.attendance.checkInAt) : '—') },
          { header: 'Out', sort: (row) => row.attendance?.checkOutAt ?? '', cell: (row) => (row.attendance?.checkOutAt ? timeOnly(row.attendance.checkOutAt) : '—') },
          {
            header: 'Late',
            align: 'right',
            sort: (row) => row.attendance?.lateMinutes ?? 0,
            cell: (row) =>
              row.attendance?.lateMinutes ? <span className="text-destructive">{minutesLabel(row.attendance.lateMinutes)}</span> : '—',
          },
          {
            header: 'Overtime',
            align: 'right',
            hideBelow: 'sm',
            sort: (row) => row.attendance?.overtimeMinutes ?? 0,
            cell: (row) => minutesLabel(row.attendance?.overtimeMinutes ?? 0),
          },
          {
            header: 'Status',
            sort: (row) => effectiveStatus(row),
            cell: (row) => {
              const status = effectiveStatus(row);
              if (status === 'unmarked') return <StatusBadge status="unmarked" label="Not marked" tone="muted" />;
              return (
                <span className="inline-flex flex-wrap items-center gap-1">
                  <StatusBadge status={status} tone={status === 'present' ? 'success' : status === 'absent' ? 'destructive' : status === 'leave' ? 'info' : undefined} />
                  {row.leaveType && <span className="text-xs text-muted-foreground">{humanize(row.leaveType)}</span>}
                </span>
              );
            },
          },
        ]}
        rowActions={
          manage
            ? (row) => {
                const status = effectiveStatus(row);
                const pending = busy === row.employee.id;
                const workDate = date;
                return (
                  <>
                    {status !== 'present' && (
                      <Button size="sm" variant="ghost" loading={pending} onClick={() => mark.mutate({ employeeId: row.employee.id, workDate, status: 'present' })}>
                        Present
                      </Button>
                    )}
                    {status !== 'absent' && (
                      <Button size="sm" variant="ghost" loading={pending} onClick={() => mark.mutate({ employeeId: row.employee.id, workDate, status: 'absent' })}>
                        Absent
                      </Button>
                    )}
                    {!row.attendance?.checkInAt && status !== 'absent' && status !== 'leave' && (
                      <Button
                        size="sm"
                        variant="outline"
                        loading={pending}
                        onClick={() => mark.mutate({ employeeId: row.employee.id, workDate, status: 'present', checkInAt: new Date().toISOString() })}
                      >
                        In now
                      </Button>
                    )}
                    {row.attendance?.checkInAt && !row.attendance.checkOutAt && (
                      <Button
                        size="sm"
                        variant="outline"
                        loading={pending}
                        onClick={() =>
                          mark.mutate({ employeeId: row.employee.id, workDate, status: row.attendance?.status ?? 'present', checkOutAt: new Date().toISOString() })
                        }
                      >
                        Out now
                      </Button>
                    )}
                  </>
                );
              }
            : undefined
        }
      />
    </>
  );
}

function MonthlySummary() {
  const [month, setMonth] = useState(currentMonth);
  const range = monthRange(month);

  const summary = useQuery({
    queryKey: ['attendance', 'summary', month],
    queryFn: () => get<SummaryRow[]>(`/hr/attendance/summary${qs({ from: `${range.from}T00:00:00.000Z`, to: `${range.to}T23:59:59.999Z` })}`),
  });

  const rows = summary.data ?? [];
  const total = (pick: (row: SummaryRow) => number) => rows.reduce((sum, row) => sum + pick(row), 0);

  return (
    <>
      <FilterBar>
        <FilterField label="Month">
          <MonthPicker value={month} onChange={setMonth} className="w-44" />
        </FilterField>
      </FilterBar>

      <DataTable
        rows={rows}
        loading={summary.isLoading}
        refreshing={summary.isFetching}
        error={summary.error}
        rowKey={(row) => row.employeeId}
        empty="Nothing recorded this month."
        defaultSort={{ column: 'Person', direction: 'asc' }}
        columns={[
          {
            header: 'Person',
            sort: (row) => row.name,
            cell: (row) => (
              <div>
                <p>{row.name}</p>
                <p className="text-xs font-normal text-muted-foreground">{row.employeeNo}</p>
              </div>
            ),
          },
          { header: 'Present', align: 'right', sort: (row) => row.present, cell: (row) => row.present || '—' },
          { header: 'Half days', align: 'right', hideBelow: 'sm', sort: (row) => row.half_day, cell: (row) => row.half_day || '—' },
          {
            header: 'Absent',
            align: 'right',
            sort: (row) => row.absent,
            cell: (row) => (row.absent ? <span className="font-medium text-destructive">{row.absent}</span> : '—'),
          },
          { header: 'Leave', align: 'right', sort: (row) => row.leave, cell: (row) => row.leave || '—' },
          {
            header: 'Late days',
            align: 'right',
            sort: (row) => row.lateDays,
            cell: (row) =>
              row.lateDays ? (
                <span className="text-warning">
                  {row.lateDays}
                  <span className="block text-xs font-normal text-muted-foreground">{minutesLabel(row.lateMinutes)}</span>
                </span>
              ) : (
                '—'
              ),
          },
          { header: 'Overtime', align: 'right', sort: (row) => row.overtimeMinutes, cell: (row) => hoursLabel(row.overtimeMinutes) },
        ]}
        footer={
          rows.length > 0 && (
            <div className="flex flex-wrap gap-x-5 gap-y-1 tabular">
              <span>{rows.length} people</span>
              <span>Present {total((row) => row.present)}</span>
              <span>Absent {total((row) => row.absent)}</span>
              <span>Leave {total((row) => row.leave)}</span>
              <span>Late days {total((row) => row.lateDays)}</span>
              <span>Overtime {hoursLabel(total((row) => row.overtimeMinutes))}</span>
            </div>
          )
        }
      />
    </>
  );
}

export function AttendanceTab() {
  const { can } = useAuth();
  const [view, setView] = useState<'day' | 'month'>('day');
  const showSummary = can('hr.attendance.manage', 'hr.payroll.run');

  return (
    <>
      {showSummary && (
        <Tabs
          help={false}
          active={view}
          onChange={(id) => setView(id === 'month' ? 'month' : 'day')}
          tabs={[
            { id: 'day', label: 'Day register', icon: ClipboardList },
            { id: 'month', label: 'Monthly summary', icon: CalendarDays },
          ]}
        />
      )}
      {view === 'day' || !showSummary ? <DayRegister /> : <MonthlySummary />}
    </>
  );
}
